import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { extractStorageIdFromQr } from '../orders/orderdetail/scanningLogic';
import {
  checkOutShelfLabels,
  assignBatchToShelf,
  markLongBoxesWithoutReassign,
  getLabelByStorageIdPrefix,
} from '../../services/shelfIndexAPI';
import { updateShelfAuditFlag, createShelfAuditEvent } from '../../services/devAPI';
import { getApiErrorMessage } from '../../services/errorUtils';
import PrintLabelModal from '../receiving/PrintLabelModal';
import PrintEventScanResumeHost from '../labels/PrintEventScanResumeHost';
import { DEFAULT_PRINTER_ID } from '@/shared/types/printer.types';
import ScanQueueChips, { type ScanQueueChip } from '../shared/ScanQueueChips';

export interface ShelfAuditChipShelf {
  id: number;
  shelf_name: string;
  audit_flag: boolean | null;
  audited_at?: string | null;
  /** Prefixes confirmed on the open-audit event, when this confirm just wrote them. */
  existing_ids?: readonly string[];
}

export interface ShelfAuditChipItem {
  id: string;
  sku: string;
  type: string;
  shelf_index: string;
}

/** Prepared next-shelf claim. `apply` swaps the walk UI; call it only after the success overlay. */
export interface ShelfAuditAdvanceSession {
  apply: () => void;
}

interface ShelfAuditChipProps {
  shelf: ShelfAuditChipShelf;
  items: ShelfAuditChipItem[];
  itemsLoading: boolean;
  /** Open this shelf already in scan mode (post-submit advance). */
  startInScanMode?: boolean;
  /**
   * When set, scan mode opens with these prefixes already in the queue.
   * Null leaves the queue empty. An empty array is an audited empty shelf.
   */
  initialExistingIds?: readonly string[] | null;
  canScanBack: boolean;
  canScanForward: boolean;
  onScanBack: () => void;
  onScanForward: () => void;
  onPassAndAdvance: (updatedShelf: ShelfAuditChipShelf) => Promise<ShelfAuditAdvanceSession>;
  onError: (message: string) => void;
  onLabelClick: (item: ShelfAuditChipItem) => void;
  onSkuClick: (sku: string) => void;
  onScanModeChange: (open: boolean) => void;
  /** Camera-preview message. Null clears it. */
  onScanCameraMessage: (message: string | null) => void;
  /** True while Submit shows Submitting... */
  onSubmittingChange: (submitting: boolean) => void;
}

const SUCCESS_FLASH_MS = 1000;

export interface ShelfAuditChipHandle {
  ingestScan: (data: string | null) => boolean;
  /** Enter scan mode the same way the header Scan control used to. */
  enterScanMode: () => void;
}

function normalizePrefix(id: string): string {
  return String(id || '').trim().toLowerCase().replace(/-/g, '').substring(0, 6);
}

function formatAuditedOn(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function prefixSet(ids: readonly string[]): Set<string> {
  const set = new Set<string>();
  for (const id of ids) {
    const prefix = normalizePrefix(id);
    if (prefix.length === 6) set.add(prefix);
  }
  return set;
}

function samePrefixSet(left: ReadonlySet<string>, right: ReadonlySet<string>): boolean {
  if (left.size !== right.size) return false;
  for (const prefix of left) {
    if (!right.has(prefix)) return false;
  }
  return true;
}

function inventoryBlocksAdvance(
  result: { readonly failed: readonly { message: string }[]; readonly assigned: number },
  prefixCount: number,
): boolean {
  return result.failed.length > 0 && result.assigned === 0 && prefixCount > 0;
}

function formatExpectedLabelCount(count: number): string {
  return `${count} ${count === 1 ? 'label' : 'labels'}`;
}

function chipsFromExistingIds(
  ids: readonly string[],
  items: readonly ShelfAuditChipItem[],
): ScanQueueChip[] {
  const chips: ScanQueueChip[] = [];
  const seen = new Set<string>();
  for (const id of ids) {
    const prefix = normalizePrefix(id);
    if (prefix.length !== 6 || seen.has(prefix)) continue;
    seen.add(prefix);
    const knownSku = items.find((item) => normalizePrefix(item.id) === prefix)?.sku?.trim();
    chips.push(knownSku ? { prefix, sku: knownSku } : { prefix });
  }
  return chips;
}

function collectExpectedIds(items: ShelfAuditChipItem[]): string[] {
  return [...new Set(
    items
      .map((item) => normalizePrefix(item.id))
      .filter((id) => id.length === 6)
  )];
}

const ShelfAuditChip = forwardRef<ShelfAuditChipHandle, ShelfAuditChipProps>(function ShelfAuditChip({
  shelf,
  items,
  itemsLoading,
  startInScanMode = false,
  initialExistingIds = null,
  canScanBack,
  canScanForward,
  onScanBack,
  onScanForward,
  onPassAndAdvance,
  onError,
  onLabelClick,
  onSkuClick,
  onScanModeChange,
  onScanCameraMessage,
  onSubmittingChange,
}, ref) {
  const [rescanMode, setRescanMode] = useState(startInScanMode);
  const [expectedIds, setExpectedIds] = useState<string[]>(() =>
    startInScanMode && !itemsLoading ? collectExpectedIds(items) : []
  );
  const [scanQueue, setScanQueue] = useState<ScanQueueChip[]>([]);
  const scanQueueRef = useRef<ScanQueueChip[]>([]);
  scanQueueRef.current = scanQueue;
  const [confirmLoading, setConfirmLoading] = useState(false);
  const confirmLoadingRef = useRef(false);
  const [successFlash, setSuccessFlash] = useState(false);
  const [scanFlash, setScanFlash] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const aliveRef = useRef(true);
  const cancelFlashWaitRef = useRef<(() => void) | null>(null);
  const rescanModeRef = useRef(startInScanMode);
  rescanModeRef.current = rescanMode;
  const awaitingScanItemsRef = useRef(startInScanMode && itemsLoading);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      cancelFlashWaitRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (!awaitingScanItemsRef.current || itemsLoading) return;
    awaitingScanItemsRef.current = false;
    setExpectedIds(collectExpectedIds(items));
    if (initialExistingIds == null) return;
    const chips = chipsFromExistingIds(initialExistingIds, items);
    scanQueueRef.current = chips;
    setScanQueue(chips);
    for (const chip of chips) {
      if (chip.sku) continue;
      void getLabelByStorageIdPrefix(chip.prefix).then((label) => {
        const sku = label?.sku?.trim();
        if (!sku || !aliveRef.current) return;
        setScanQueue((prev) =>
          prev.map((item) => (item.prefix === chip.prefix && !item.sku ? { prefix: chip.prefix, sku } : item))
        );
      });
    }
  }, [items, itemsLoading, initialExistingIds]);

  const waitForSuccessFlash = (remainingMs: number): Promise<void> => {
    if (remainingMs <= 0) return Promise.resolve();
    return new Promise((resolve) => {
      const timerId = window.setTimeout(() => {
        cancelFlashWaitRef.current = null;
        resolve();
      }, remainingMs);
      cancelFlashWaitRef.current = () => {
        window.clearTimeout(timerId);
        cancelFlashWaitRef.current = null;
        resolve();
      };
    });
  };

  const beginScan = useCallback((): void => {
    if (itemsLoading || rescanModeRef.current) return;
    rescanModeRef.current = true;
    setExpectedIds(collectExpectedIds(items));
    scanQueueRef.current = [];
    setScanQueue([]);
    setRescanMode(true);
    onScanCameraMessage(null);
    onScanModeChange(true);
  }, [items, itemsLoading, onScanCameraMessage, onScanModeChange]);

  const handleScanCapture = useCallback((data: string | null): boolean => {
    if (confirmLoadingRef.current) return false;
    const prefix = extractStorageIdFromQr(data);
    if (!prefix) return false;
    if (scanQueueRef.current.some((item) => item.prefix === prefix)) {
      onScanCameraMessage(`ID ${prefix} has already been scanned`);
      return false;
    }
    const knownSku = items.find((item) => normalizePrefix(item.id) === prefix)?.sku?.trim();
    const nextChip: ScanQueueChip = knownSku ? { prefix, sku: knownSku } : { prefix };
    const nextQueue = [...scanQueueRef.current, nextChip];
    scanQueueRef.current = nextQueue;
    setScanQueue(nextQueue);
    onScanCameraMessage(null);
    setScanFlash(false);
    window.requestAnimationFrame(() => {
      setScanFlash(true);
      window.setTimeout(() => setScanFlash(false), 350);
    });
    if (knownSku) return true;
    void getLabelByStorageIdPrefix(prefix).then((label) => {
      const sku = label?.sku?.trim();
      if (!sku) return;
      setScanQueue((prev) => {
        const next = prev.map((item) => (item.prefix === prefix && !item.sku ? { prefix, sku } : item));
        scanQueueRef.current = next;
        return next;
      });
    });
    return true;
  }, [items, onScanCameraMessage]);

  useImperativeHandle(ref, () => ({
    ingestScan: handleScanCapture,
    enterScanMode: beginScan,
  }), [handleScanCapture, beginScan]);

  const handleRemoveFromQueue = (prefix: string): void => {
    setScanQueue((prev) => {
      const next = prev.filter((item) => item.prefix !== prefix);
      scanQueueRef.current = next;
      return next;
    });
  };

  const handleConfirm = async (overrides?: { expected_ids?: string[]; existing_ids?: string[] }) => {
    const expected = overrides?.expected_ids ?? expectedIds;
    const existing = overrides?.existing_ids ?? scanQueue.map((item) => item.prefix);
    try {
      confirmLoadingRef.current = true;
      onSubmittingChange(true);
      setConfirmLoading(true);
      await createShelfAuditEvent({
        shelf_name: shelf.shelf_name,
        shelf_id: shelf.id,
        expected_ids: expected,
        existing_ids: existing,
      });

      const onShelf = prefixSet(items.map((item) => item.id));
      const submitted = prefixSet(existing);
      if (!samePrefixSet(onShelf, submitted)) {
        await checkOutShelfLabels(shelf.shelf_name);
        const batchResult = await assignBatchToShelf(shelf.shelf_name, existing);
        if (inventoryBlocksAdvance(batchResult, submitted.size)) {
          onError(
            batchResult.failed.map((failure) => `${failure.prefix}: ${failure.message}`).join('; ') ||
              'Failed to assign scanned labels',
          );
          return;
        }
      } else if (submitted.size > 0) {
        const markResult = await markLongBoxesWithoutReassign(shelf.shelf_name, [...submitted]);
        if (inventoryBlocksAdvance(markResult, submitted.size)) {
          onError(
            markResult.failed.map((failure) => `${failure.prefix}: ${failure.message}`).join('; ') ||
              'Failed to mark long box',
          );
          return;
        }
      }

      void updateShelfAuditFlag(shelf.id, true).catch((err: unknown) => {
        onError(getApiErrorMessage(err, 'Failed to mark shelf audited'));
      });
      const startedAt = Date.now();
      if (aliveRef.current) setSuccessFlash(true);
      const session = await onPassAndAdvance({
        id: shelf.id,
        shelf_name: shelf.shelf_name,
        audit_flag: true,
        audited_at: new Date().toISOString(),
        existing_ids: existing,
      });
      if (!aliveRef.current) return;
      await waitForSuccessFlash(SUCCESS_FLASH_MS - (Date.now() - startedAt));
      if (!aliveRef.current) return;
      session.apply();
    } catch (err: unknown) {
      onError(getApiErrorMessage(err, 'Failed to confirm shelf scan'));
    } finally {
      confirmLoadingRef.current = false;
      onSubmittingChange(false);
      if (aliveRef.current) setConfirmLoading(false);
    }
  };

  const handleConfirmEmptyShelf = () => {
    void handleConfirm({ expected_ids: [], existing_ids: [] });
  };

  const handleCancelRescan = (): void => {
    if (confirmLoading || successFlash) return;
    rescanModeRef.current = false;
    setRescanMode(false);
    onScanModeChange(false);
    onScanCameraMessage(null);
    setExpectedIds([]);
    scanQueueRef.current = [];
    setScanQueue([]);
    setScanFlash(false);
  };

  return (
    <div className={`sa-chip${scanFlash ? ' sa-scan-flash' : ''}`}>
      <div className={`sa-chip-column${rescanMode ? ' sa-scan-panel' : ''}`}>
      <div className="sa-chip-header">
        <div className="sa-chip-title-block">
          <div className="sa-chip-title-row">
            <div className="sa-chip-title">{shelf.shelf_name}</div>
            {shelf.audited_at && (
              <span className="sa-audited-chip">
                Audited on: {formatAuditedOn(shelf.audited_at)}
              </span>
            )}
          </div>
          {!itemsLoading && (
            <div className="sa-chip-label-count">{formatExpectedLabelCount(items.length)}</div>
          )}
        </div>
        <div className="sa-chip-header-actions">
          {rescanMode ? (
            <>
              {canScanBack ? (
                <button
                  type="button"
                  className="sa-chip-scan-nav"
                  onClick={onScanBack}
                  disabled={confirmLoading || successFlash || itemsLoading}
                  aria-label="Previous scanned shelf"
                >
                  {'<'}
                </button>
              ) : null}
              {canScanForward ? (
                <button
                  type="button"
                  className="sa-chip-scan-nav"
                  onClick={onScanForward}
                  disabled={confirmLoading || successFlash || itemsLoading}
                  aria-label="Next scanned shelf"
                >
                  {'>'}
                </button>
              ) : null}
              <button
                type="button"
                className="sa-chip-cancel-rescan"
                onClick={handleCancelRescan}
                disabled={confirmLoading || successFlash}
                aria-label="Cancel scan"
              >
                &times;
              </button>
            </>
          ) : (
            <button
              type="button"
              className="dev-panel-button sa-chip-rescan-btn"
              onClick={() => setPrintModalOpen(true)}
              disabled={itemsLoading}
            >
              Print
            </button>
          )}
        </div>
      </div>

      <PrintLabelModal
        isOpen={printModalOpen}
        onClose={() => setPrintModalOpen(false)}
        shelfIndex={shelf.shelf_name}
      />
      <PrintEventScanResumeHost source="receiving_modal" printerId={DEFAULT_PRINTER_ID} />

      <div className="sa-chip-body">
      {successFlash && (
        <div className="success-flash">
          <div className="success-message">✓ Success!</div>
        </div>
      )}

      {itemsLoading ? (
        <div className="sw-results-empty">Loading...</div>
      ) : rescanMode ? (
        <>
          <p className="sa-chip-rescan-hint">
            Scan QR codes for labels present on this shelf. On Submit, inventory is updated to match
            the scan and an audit event is recorded.
          </p>
          <ScanQueueChips
            items={scanQueue}
            onRemove={handleRemoveFromQueue}
          />
        </>
      ) : items.length === 0 ? (
        <div className="sw-results-empty">No items found</div>
      ) : (
        <div className="sw-table-scroll">
          <table className="sw-results-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>SKU</th>
                <th>Type</th>
                <th>Index</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={`${item.sku}-${item.id}-${idx}`}>
                  <td className="sw-results-id-cell">
                    <button
                      type="button"
                      className="sw-results-id-clickable"
                      onClick={() => onLabelClick(item)}
                    >
                      {item.id}
                    </button>
                  </td>
                  <td
                    className="sw-sku-clickable"
                    onClick={() => onSkuClick(item.sku)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSkuClick(item.sku);
                      }
                    }}
                  >
                    {item.sku}
                  </td>
                  <td className="sw-results-type-cell">{item.type}</td>
                  <td className="sw-results-index-cell">{item.shelf_index}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>

      {rescanMode ? (
        <div className="sa-chip-confirm">
          <button
            type="button"
            className="sa-confirm-btn"
            onClick={() => { void handleConfirm(); }}
            disabled={confirmLoading || successFlash || itemsLoading}
          >
            {confirmLoading ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      ) : null}
      </div>

      {!itemsLoading && !rescanMode && items.length === 0 ? (
        <div className="sa-chip-confirm">
          <button
            type="button"
            className="sa-confirm-btn"
            onClick={handleConfirmEmptyShelf}
            disabled={confirmLoading || successFlash}
          >
            {confirmLoading ? 'Confirming...' : 'Confirm'}
          </button>
        </div>
      ) : null}
    </div>
  );
});

export default ShelfAuditChip;
